import { REPORT_POLL_TIMEOUT_MS, pollForReport, regenerateRedline } from "../../utils/api";
import { getBookmarkedSectionsOoxml } from "../../utils/document";
import { type RedlineTextProposal, redlineTextProposalSchema } from "@common/utils/redline/wire/redline-payload";
import { useCallback, useEffect, useState } from "react";
import type React from "react";
import type { ErrorContext } from "src/context/AnalyticsContext";
import { getOriginalActionIndex, getOriginalStepIndex, getValidRedlineActionsAndSteps } from "./rule-action-plan";

export const DEFAULT_CURRENT_ACTION_INDEX = 0;

export const DEFAULT_CURRENT_STEP_INDEX = 0;

export const DEFAULT_CURRENT_COMMENT_ACTION_INDEX = 0;

export const DEFAULT_CURRENT_COMMENT_STEP_INDEX = 0;

export interface UseActionNavigationInputs {
  selectedRuleUuid: string;
  currentActionPlan: any;
  authHeader: string;
  currentReviewId: string;
  selectedRule: any;
  updateActionTexts: (ruleUuid: string, actionIndex: number, stepIndex: number, textRedline: RedlineTextProposal) => void;
  updateActionOoxml: (ruleUuid: string, actionIndex: number, stepIndex: number, ooxmlBefore: string[], ooxmlAfter: string[]) => void;
  setParagraphs: React.Dispatch<React.SetStateAction<any[]>>;
  setIsParagraphsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  sendError: (error: unknown, context?: ErrorContext | Record<string, any>) => void;
}

export function useActionNavigation({ selectedRuleUuid, currentActionPlan, authHeader, currentReviewId, selectedRule, updateActionTexts, updateActionOoxml, setParagraphs, setIsParagraphsLoading, sendError }: UseActionNavigationInputs) {
    const [currentActionIndex, setCurrentActionIndex] = useState(DEFAULT_CURRENT_ACTION_INDEX);
    const [currentStepIndex, setCurrentStepIndex] = useState(DEFAULT_CURRENT_STEP_INDEX);
    const [currentCommentActionIndex, setCurrentCommentActionIndex] = useState(DEFAULT_CURRENT_COMMENT_ACTION_INDEX);
    const [currentCommentStepIndex, setCurrentCommentStepIndex] = useState(DEFAULT_CURRENT_COMMENT_STEP_INDEX);
    const [isRegenerating, setIsRegenerating] = useState<number | null>(null);
    const [showFeedbackForm, setShowFeedbackForm] = useState<number | null>(null);
    const resetNavigationIndices = useCallback(() => {
        setCurrentActionIndex(DEFAULT_CURRENT_ACTION_INDEX);
        setCurrentStepIndex(DEFAULT_CURRENT_STEP_INDEX);
        setCurrentCommentActionIndex(DEFAULT_CURRENT_COMMENT_ACTION_INDEX);
        setCurrentCommentStepIndex(DEFAULT_CURRENT_COMMENT_STEP_INDEX);
    }, []);
    useEffect(() => {
        resetNavigationIndices();
    }, [selectedRuleUuid, resetNavigationIndices]);
    const handleRegenerate = useCallback(async (actionIndex: number, stepIndex: number, feedback: string) => {
        try {
            setIsRegenerating(actionIndex);

            if (!authHeader) return;
            if (!currentActionPlan || currentActionPlan.length === 0) return;

            const { validActions, validStepsMap } = getValidRedlineActionsAndSteps(currentActionPlan);
            const currentAction = validActions[actionIndex];
            const validSteps = validStepsMap.get(currentAction) || [];
            const currentStep = validSteps[stepIndex];
            const actionReport = currentStep.action_report;

            if (!actionReport.target_bookmark_names?.length) {
                throw new Error("No bookmarks found for regeneration");
            }

            const current_section_ooxml = await getBookmarkedSectionsOoxml(actionReport.target_bookmark_names);
            if (!current_section_ooxml) {
                throw new Error("Could not get current section OOXML");
            }

            const previousTexts = actionReport.target_texts;
            const incoming_section_ooxml = actionReport.target_ooxml_after?.join('') || '';
            if (!incoming_section_ooxml && !previousTexts) {
                throw new Error("No previous modified section OOXML found");
            }

            const original_section_ooxml = actionReport.target_ooxml_before?.join('') || '';
            if (!original_section_ooxml && !previousTexts) {
                throw new Error("No previous section OOXML found");
            }

            const regenerate_context = currentStep.regenerate_context || '';
            if (!regenerate_context) {
                throw new Error("No regenerate context found");
            }

            const originalActionIdx = getOriginalActionIndex(actionIndex, validActions, currentActionPlan);
            const originalStepIdx = getOriginalStepIndex(stepIndex, validSteps, currentAction);

            const { report_file } = await regenerateRedline(
                authHeader,
                current_section_ooxml,
                incoming_section_ooxml,
                original_section_ooxml,
                regenerate_context,
                feedback,
                previousTexts ?? null,
                currentReviewId,
                selectedRule!.rule.uuid,
                originalActionIdx,
                originalStepIdx,
            );

            const result = await pollForReport(report_file, REPORT_POLL_TIMEOUT_MS);

            if (result?.status === "DONE" && result?.redline) {
                updateActionTexts(
                    selectedRule!.rule.uuid,
                    originalActionIdx,
                    originalStepIdx,
                    redlineTextProposalSchema.parse(result.redline)
                );
            } else if (result?.status === "DONE" && result?.section_ooxml) {
                updateActionOoxml(
                    selectedRule!.rule.uuid,
                    originalActionIdx,
                    originalStepIdx,
                    [current_section_ooxml],
                    [result.section_ooxml]
                );
            } else if (result?.status === 'ERROR') {
                throw new Error(result.error || 'Failed to regenerate section');
            } else {
                throw new Error('Invalid response from regeneration API');
            }

            setShowFeedbackForm(null);

            setParagraphs(null);
            setIsParagraphsLoading(true);
        } catch (error) {
            console.error("Error regenerating redline:", error);
            sendError(error, {
                component: "RuleDetailsPage",
                action: "handleRegenerate",
                actionIndex: actionIndex,
                stepIndex: stepIndex
            });
        } finally {
            setIsRegenerating(null);
        }
    }, [authHeader, currentActionPlan, sendError, selectedRule, updateActionOoxml, updateActionTexts, currentReviewId, setIsParagraphsLoading, setParagraphs]);
    const handlePrevRedlineStep = useCallback(() => {
        if (!currentActionPlan) return;

        const { validActions, validStepsMap } = getValidRedlineActionsAndSteps(currentActionPlan);

        if (currentStepIndex > 0) {
            setCurrentStepIndex(currentStepIndex - 1);
        } else if (currentActionIndex > 0) {
            const prevAction = validActions[currentActionIndex - 1];
            const prevSteps = validStepsMap.get(prevAction) || [];
            setCurrentActionIndex(currentActionIndex - 1);
            setCurrentStepIndex(prevSteps.length - 1);
        }
    }, [currentActionIndex, currentStepIndex, currentActionPlan]);
    const handleNextRedlineStep = useCallback(() => {
        if (!currentActionPlan) return;

        const { validActions, validStepsMap } = getValidRedlineActionsAndSteps(currentActionPlan);
        const currentAction = validActions[Math.min(currentActionIndex, validActions.length - 1)];
        const validSteps = validStepsMap.get(currentAction) || [];
        const totalSteps = validSteps.length;
        const totalActions = validActions.length;

        if (currentStepIndex < totalSteps - 1) {
            setCurrentStepIndex(currentStepIndex + 1);
        } else if (currentActionIndex < totalActions - 1) {
            setCurrentActionIndex(currentActionIndex + 1);
            setCurrentStepIndex(DEFAULT_CURRENT_STEP_INDEX);
        }
    }, [currentActionIndex, currentStepIndex, currentActionPlan]);
    const handleRegenerateCallback = useCallback(async (feedback: string) => {
        await handleRegenerate(currentActionIndex, currentStepIndex, feedback);
    }, [currentActionIndex, currentStepIndex, handleRegenerate]);
    const handleToggleFeedbackCallback = useCallback(async () => {
        setShowFeedbackForm(showFeedbackForm === currentActionIndex ? null : currentActionIndex);
    }, [showFeedbackForm, currentActionIndex]);
  return { currentActionIndex, currentStepIndex, currentCommentActionIndex, currentCommentStepIndex, isRegenerating, showFeedbackForm, handlePrevRedlineStep, handleNextRedlineStep, handleRegenerateCallback, handleToggleFeedbackCallback, setCurrentCommentActionIndex, setCurrentCommentStepIndex, resetNavigationIndices };
}
